import { useEffect, useRef, useState } from 'react';

import {
  fetchPlanDetails,
  fetchSettingsApi,
  selectPlanDetails,
  selectSettings,
} from 'src/features/settings-slice';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { Box, Heading, Text } from 'wix-style-react';
import { Line } from 'rc-progress';
import styles from './PlanInformation.module.scss';
import { selectSiteData } from 'src/features/account-slice';
import { useSelector } from 'react-redux';
import AttendeeTransactionWarning from 'src/assets/attendee-transaction-warning.gif';
import { capitalize } from 'lodash';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom';
import { buildURL } from 'src/features/utils';
import { selectSite } from 'src/features/site-slice';

// Return color based on usage percentage - green to amber to red
function getColorForPercentage(percentage) {
  if (percentage >= 95) {
    return '#FF5252'; // Bright red for critical - more noticeable
  } else if (percentage > 80) {
    return '#FFA726'; // Orange/amber for warning
  } else if (percentage >= 60) {
    return '#42A5F5'; // Blue for moderate usage
  } else {
    return '#66BB6A'; // Green for healthy levels
  }
}

export const AttendeeQuotaAmount = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const planDetails = useAppSelector(selectPlanDetails);
  const settings = useAppSelector(selectSettings);
  const site = useSelector(selectSite);
  const op = useRef(null);
  const history = useHistory();
  const hasEventCreatorRole = site?.roles?.includes('EVENT_CREATOR');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      await dispatch(fetchPlanDetails());
      await dispatch(fetchSettingsApi());

      setLoading(false);
    }

    if (!hasEventCreatorRole) {
      void fetchData();
    }
  }, [dispatch, hasEventCreatorRole]);

  const transactionCount = planDetails?.monthly_attendee_transaction;
  const emailsSentCount = planDetails?.monthly_emails_sent;
  const quota = planDetails?.monthly_quota;
  const emailQuota = planDetails?.monthly_email_sent_quota;
  const quotaPercentage = (+transactionCount / +quota) * 100;
  const emaiQuotaPercentage = (+emailsSentCount / +emailQuota) * 100;

  const upgradeLink = `/events/${site?.id}/billing`;

  const formatPlanName = (planName: string | undefined): string => {
    if (!planName) return '';

    // Check if the plan name contains a hyphen and a number
    const match = /^(enterprise)-(\d+)$/i.exec(planName);
    if (match) {
      const [_, name, number] = match;
      const formattedName = capitalize(name);
      const formattedNumber = Number(number).toLocaleString();
      return `${formattedName} ${formattedNumber}`;
    }

    return capitalize(planName);
  };

  const label = planDetails?.in_free_trial
    ? `Free Trial (${settings?.trialDaysLeft} Days Left)`
    : `${formatPlanName(planDetails?.plan_name)} Plan`;
  return (
    <>
      <Box direction="vertical">
        <Box align="right">
          <Button
            className={styles.businessPlanButton}
            label={loading ? 'Loading Plan...' : `${label} • View Usage`}
            icon="pi pi-chart-bar"
            iconPos="left"
            onClick={(e) => op.current.toggle(e)}
            loading={loading}
          />
        </Box>
      </Box>
      {!loading && (
        <OverlayPanel ref={op}>
          <Box direction="vertical">
            <Box gap={2} marginTop={3} marginBottom={3} verticalAlign="middle" direction="vertical">
              <Box verticalAlign="middle" gap={2} direction="vertical">
                <Box direction="horizontal" align="space-between" width="100%">
                  <Heading appearance="H5">Attendee Usage</Heading>
                  <Text size="small" weight="thin" secondary>
                    Resets in{' '}
                    {new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate() -
                      new Date().getDate()}{' '}
                    days
                  </Text>
                </Box>

                <Box width="350" height={15}>
                  <Line
                    percent={quotaPercentage}
                    strokeWidth={3}
                    trailColor="#f0f0f0"
                    strokeColor={getColorForPercentage(quotaPercentage)}
                    strokeLinecap="round"
                  />
                </Box>

                <Box direction="horizontal" align="space-between" width="100%">
                  <Text size="medium">
                    <b>{transactionCount}</b> of <b>{quota}</b> attendees
                  </Text>
                  <Text
                    size="medium"
                    weight="bold"
                    style={{ color: getColorForPercentage(quotaPercentage) }}
                  >
                    {Math.round(quotaPercentage)}%
                  </Text>
                </Box>

                {quotaPercentage > 90 && (
                  <Box
                    className={styles.warningMessage}
                    align="center"
                    verticalAlign="middle"
                    gap={1}
                  >
                    <i
                      className="pi pi-exclamation-triangle"
                      style={{ color: '#FF5252', fontSize: '1.2rem' }}
                    />
                    <Text size="small">You&apos;re close to your monthly limit!</Text>
                  </Box>
                )}
              </Box>
              {planDetails.plan_name !== 'free' && emailQuota !== -1 && (
                <Box verticalAlign="middle" gap={2}>
                  <Box direction="horizontal" align="space-between" width="100%">
                    <Heading appearance="H5">Email Usage</Heading>
                    <Text size="small" weight="thin" secondary>
                      Resets monthly
                    </Text>
                  </Box>

                  <Box width="350" height={15}>
                    <Line
                      percent={emaiQuotaPercentage}
                      strokeWidth={3}
                      trailColor="#f0f0f0"
                      strokeColor={getColorForPercentage(emaiQuotaPercentage)}
                      strokeLinecap="round"
                    />
                  </Box>

                  <Box direction="horizontal" align="space-between" width="100%">
                    <Text size="medium">
                      <b>{emailsSentCount}</b> of <b>{emailQuota}</b> emails
                    </Text>
                    <Text
                      size="medium"
                      weight="bold"
                      style={{ color: getColorForPercentage(emaiQuotaPercentage) }}
                    >
                      {Math.round(emaiQuotaPercentage)}%
                    </Text>
                  </Box>

                  {emaiQuotaPercentage > 90 && (
                    <Box
                      className={styles.warningMessage}
                      align="center"
                      verticalAlign="middle"
                      gap={1}
                    >
                      <i
                        className="pi pi-exclamation-triangle"
                        style={{ color: '#FF5252', fontSize: '1.2rem' }}
                      />
                      <Text size="small">Your email limit is almost reached!</Text>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            <Box align="center" marginTop={3}>
              <Button
                className={styles.upgradeButton}
                label="Need More? Upgrade Your Plan"
                icon="pi pi-arrow-up"
                iconPos="left"
                onClick={() => history.push(buildURL({}, upgradeLink))}
              />
            </Box>
          </Box>
        </OverlayPanel>
      )}
    </>
  );
};
